import { t } from "../../../i18n";
import { formatCurrency } from "../../../lib/format";
import { Icon, Stack } from "../../ui";
import type { SettlementYear } from "../utils";
import { SettlementCard } from "./SettlementCard";

interface SettlementYearGroupProps {
  group: SettlementYear;
  defaultOpen: boolean;
  onSelect: (id: string) => void;
  search?: string;
}

/**
 * Collapsible `<details>` section grouping settlements by their endDate
 * year. The most recent year is open by default; older years are
 * collapsed so they don't dominate the scroll.
 */
export function SettlementYearGroup({
  group,
  defaultOpen,
  onSelect,
  search = "",
}: SettlementYearGroupProps) {
  return (
    <details open={defaultOpen} className="group">
      <summary className="-mx-2 flex cursor-pointer select-none list-none items-center justify-between gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-ink-50 dark:hover:bg-ink-900/40 [&::-webkit-details-marker]:hidden">
        <div className="flex min-w-0 items-baseline gap-3">
          <span className="tabular font-semibold text-ink-900 text-lg">{group.year}</span>
          <span className="truncate text-ink-500 text-xs">
            {t.household.settlements.yearSummary(
              group.settlements.length,
              formatCurrency(group.total, group.currency),
            )}
          </span>
        </div>
        <Icon
          name="chevron-right"
          size="sm"
          className="shrink-0 text-ink-400 transition-transform duration-150 group-open:rotate-90"
        />
      </summary>
      <div className="mt-3 border-ink-100 border-l pl-3 dark:border-ink-800/60">
        <Stack gap={2}>
          {group.settlements.map((s) => (
            <SettlementCard
              key={s.id}
              settlement={s}
              onClick={() => onSelect(s.id)}
              search={search}
            />
          ))}
        </Stack>
      </div>
    </details>
  );
}
